/**
 * O cursor na caixa da mensagem — a parte que o chat interno, o atendimento e o
 * portal do parceiro compartilham.
 *
 * Pedido do Augusto (14/10/2026), depois do colar imagem (#785): "quando a
 * pessoa clica na conversa, instintivamente dá um Ctrl+V para colar a imagem.
 * Só que o foco não está na caixa da mensagem, a imagem não é colada, e a
 * pessoa acha que não funciona." O cursor ficava no item da lista que ela
 * clicou.
 *
 * ## As três regras
 *
 * 1. **Abrir a conversa leva o cursor à caixa** — trocar de conversa (`conversa`
 *    muda), clicar de novo na que já está aberta (`pedido` muda) ou a caixa que
 *    nasce travada e destrava depois (o atendimento carregando). Uma vez por
 *    abertura: o `pendente` é consumido no primeiro foco.
 * 2. **A caixa destravou e ninguém ficou com o cursor → ele volta.** O navegador
 *    tira o foco do campo que fica `disabled` (upload do anexo, envio no
 *    portal); sem isto, o Enter depois do upload não fazia nada. Se a pessoa foi
 *    escrever em outro campo (a legenda do anexo), o cursor fica lá.
 * 3. **Ctrl+V (ou Cmd+V) com o cursor fora de qualquer campo cola na caixa.** O
 *    navegador decide onde colar DEPOIS do `keydown`: mover o foco aqui faz a
 *    colagem cair na caixa, e a imagem segue pelo `onPaste` de sempre
 *    (`useColagemDeArquivos`). Cobre quem clicou no meio da conversa para ler e
 *    depois colou. Com duas caixas na tela, ganha a mais perto do último clique
 *    (a da conversa, se ele foi na conversa; a da thread, se foi na thread).
 *
 * Abrir com rascunho põe o cursor no FIM do texto: o Chrome monta a caixa com
 * o caret em 0, e o que a pessoa digitasse entraria antes do rascunho.
 *
 * ## Por que não no celular
 *
 * As regras 1 e 2 só valem com mouse ou touchpad (`pointer: fine`). Na tela de
 * toque, o foco abre o teclado por cima de metade da conversa, e quem toca numa
 * conversa quer ler — é o que o WhatsApp faz no celular. A regra 3 vale em
 * qualquer aparelho: Ctrl+V já é prova de teclado físico.
 */
import { useEffect, useRef, type RefObject } from "react";

type Caixa = RefObject<HTMLTextAreaElement | null>;

/**
 * As caixas montadas, na ordem em que abriram. Uma escuta só para a página
 * inteira: no /mensagens convivem a caixa do chat interno (escondida atrás da
 * aba Atendimento externo), a do atendimento e a da thread por cima da conversa.
 * O Ctrl+V vai para a última que abriu, entre as visíveis e livres.
 */
const caixasAbertas: Caixa[] = [];

/** Onde foi o último clique (ou toque) — decide entre duas caixas visíveis. */
let ultimoToque: Element | null = null;

function temMouse(): boolean {
  return window.matchMedia?.("(pointer: fine)")?.matches === true;
}

function ninguemComOCursor(): boolean {
  const ativo = document.activeElement;
  return !ativo || ativo === document.body;
}

/** Campos `<input>` que não recebem colagem: o Ctrl+V com eles focados vai para a caixa. */
const ENTRADAS_SEM_TEXTO = new Set([
  "checkbox",
  "radio",
  "button",
  "submit",
  "reset",
  "file",
  "image",
  "range",
  "color",
]);

function ehCampoDeEscrita(el: Element | null): boolean {
  if (!el) return false;
  if (el instanceof HTMLInputElement) return !ENTRADAS_SEM_TEXTO.has(el.type);
  if (el instanceof HTMLTextAreaElement) return true;
  return el instanceof HTMLElement && el.isContentEditable;
}

/** Diálogo, menu ou popover aberto: o Ctrl+V é dele, não da conversa por trás. */
function temCamadaPorCima(ativo: Element | null): boolean {
  if (ativo?.closest('[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]')) {
    return true;
  }
  return !!document.querySelector(
    '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]'
  );
}

function estaVisivel(el: HTMLElement): boolean {
  // `checkVisibility` enxerga o `display: none` de um ancestral (a aba
  // escondida); o fallback cobre navegador antigo — sem caixa na tela, nenhum
  // retângulo.
  if (typeof el.checkVisibility === "function") return el.checkVisibility();
  return el.getClientRects().length > 0;
}

/**
 * Quão perto do último clique a caixa está: a profundidade do menor ancestral
 * que contém os dois. Sem clique registrado, todas empatam (0).
 */
function proximidade(el: HTMLElement, alvo: Element | null): number {
  if (!alvo || !alvo.isConnected) return 0;
  let comum: Node | null = el;
  while (comum && !comum.contains(alvo)) comum = comum.parentNode;
  let profundidade = 0;
  for (let no = comum; no; no = no.parentNode) profundidade++;
  return profundidade;
}

function aoTeclar(e: KeyboardEvent) {
  // Modificador antes de `key`: o autofill do Chrome dispara `keydown` como um
  // `Event` simples, sem `key`. E `key` (não `code`): é a letra do teclado da
  // pessoa, não a posição física da tecla.
  if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
  if (e.key?.toLowerCase() !== "v") return;
  const ativo = document.activeElement;
  if (ehCampoDeEscrita(ativo) || temCamadaPorCima(ativo)) return;
  // Da última aberta para a primeira; no empate de proximidade, a última ganha.
  let escolhida: HTMLTextAreaElement | null = null;
  let melhor = -1;
  for (let i = caixasAbertas.length - 1; i >= 0; i--) {
    const el = caixasAbertas[i].current;
    if (!el || el.disabled || !estaVisivel(el)) continue;
    const perto = proximidade(el, ultimoToque);
    if (perto > melhor) {
      melhor = perto;
      escolhida = el;
    }
  }
  escolhida?.focus({ preventScroll: true });
}

function aoTocar(e: Event) {
  ultimoToque = e.target instanceof Element ? e.target : null;
}

function registrar(caixa: Caixa): () => void {
  if (caixasAbertas.length === 0) {
    document.addEventListener("keydown", aoTeclar, true);
    document.addEventListener("pointerdown", aoTocar, true);
  }
  caixasAbertas.push(caixa);
  return () => {
    const i = caixasAbertas.indexOf(caixa);
    if (i >= 0) caixasAbertas.splice(i, 1);
    if (caixasAbertas.length === 0) {
      document.removeEventListener("keydown", aoTeclar, true);
      document.removeEventListener("pointerdown", aoTocar, true);
      ultimoToque = null;
    }
  };
}

export function useFocoNaCaixa(
  caixa: Caixa,
  {
    conversa,
    desligado,
    pedido = 0,
  }: {
    /** A conversa aberta; `null` = nenhuma (a caixa não recebe o cursor). */
    conversa: string | null | undefined;
    /** A caixa está travada (encerrada, carregando, subindo, gravando áudio). */
    desligado: boolean;
    /** Cresce a cada clique numa conversa — inclusive na que já está aberta. */
    pedido?: number;
  }
): void {
  const pendente = useRef(false);
  const estavaDesligado = useRef(false);

  useEffect(() => registrar(caixa), [caixa]);

  // Abriu (ou reabriu) a conversa: o próximo efeito leva o cursor à caixa,
  // agora ou quando ela destravar. A ordem dos dois efeitos importa.
  useEffect(() => {
    pendente.current = !!conversa;
  }, [conversa, pedido]);

  useEffect(() => {
    if (desligado) {
      estavaDesligado.current = true;
      return;
    }
    const destravou = estavaDesligado.current;
    estavaDesligado.current = false;
    const abriu = pendente.current;
    pendente.current = false;
    const el = caixa.current;
    if (!el || !temMouse()) return;
    const ativo = document.activeElement;
    if (abriu) {
      // A abertura que chega tarde (a caixa destravou depois do clique): quem
      // já foi escrever em outro campo nesse meio-tempo fica onde está.
      if (destravou && ativo !== el && ehCampoDeEscrita(ativo)) return;
      el.focus({ preventScroll: true });
      if (el.selectionStart === 0 && el.selectionEnd === 0 && el.value.length > 0) {
        el.setSelectionRange(el.value.length, el.value.length);
      }
      return;
    }
    // Destravou: o navegador restaura a seleção de antes — o caret não é mexido.
    if (destravou && ninguemComOCursor()) el.focus({ preventScroll: true });
  }, [caixa, conversa, pedido, desligado]);
}
